<template>
  <v-container class="pa-0" :class="{ 'export-embedded': embedded }" fluid>
    <v-container v-if="!embedded" class="pt-6 pb-6 px-4" fluid>
      <v-row align="center" class="ma-0">
        <v-col cols="auto" class="py-0 pr-2">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                label="Monat"
                v-model="monthFormatted"
                readonly
                outlined
                dense
                hide-details
                style="width: 150px;"
                color="#FF6600"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="month"
              no-title
              scrollable
              type="month"
              color="#FF6600"
              @input="menu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="auto" class="py-0">
          <v-text-field
            v-model="searchText"
            label="Suche"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            style="width: 250px;"
            color="#FF6600"
            @input="onSearchInput"
          ></v-text-field>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="auto" class="py-0 pr-1">
          <v-btn
            color="gray"
            class="px-2"
            height="40"
            min-width="40"
            elevation="0"
            style="border: thin solid rgba(0, 0, 0, 0.12);"
            @click="collapseAllClick"
          >
            <v-icon small color="grey darken-2">{{ expanded ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="auto" class="py-0 pr-1">
          <v-btn
            color="gray"
            class="px-2"
            height="40"
            min-width="40"
            elevation="0"
            style="border: thin solid rgba(0, 0, 0, 0.12);"
            @click="refreshDataGrid"
          >
            <v-icon small color="grey darken-2">mdi-refresh</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="auto" class="py-0">
          <v-btn
            color="gray"
            class="px-2"
            height="40"
            min-width="40"
            elevation="0"
            style="border: thin solid rgba(0, 0, 0, 0.12);"
            @click="exportToCsv"
          >
            <v-icon small color="grey darken-2">mdi-download</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
    <v-divider v-if="!embedded"></v-divider>
    <dx-data-grid
      :ref="gridRefName"
      :data-source="datasource"
      :summary="summary"
      :columns="columns"
      :show-borders="false"
      :show-column-lines="false"
      :column-auto-width="true"
      :allow-column-reordering="!gridTouch"
      :allow-column-resizing="!gridTouch"
      :hover-state-enabled="!gridTouch"
      :show-row-lines="true"
      :height="embedded ? '100%' : undefined"
      :error-row-enabled="!embedded"
      @toolbar-preparing="onToolbarPreparing($event)"
      :scrolling="scrolling"
    >
      <dx-group-panel :visible="false" />

      <dx-grouping :auto-expand-all="expanded" expandMode="rowClick" />
      <dx-paging :page-size="defaultPageSize" />
      <dx-pager
        :visible="!embedded"
        :show-page-size-selector="true"
        :allowed-page-sizes="pageSizes"
        :show-info="true"
        :info-text="'Seite {0} von {1} ({2} Verkäufe)'"
      />
      <dx-filter-row :visible="!embedded" />
      <dx-load-panel :enabled="!embedded" />
      <dx-export
        :enabled="false"
        :allow-export-selected-data="true"
        file-name="landshut"
        :texts="{
          exportAll: 'Alles herunterladen',
          exportSelectedRows: 'Auswahl herunterladen',
          exportTo: 'Excel Download',
        }"
      />
    </dx-data-grid>
  </v-container>
</template>

<script>
import {
  DxDataGrid,
  DxPaging,
  DxFilterRow,
  DxPager,
  DxExport,
  DxGrouping,
  DxGroupPanel,
  DxLoadPanel,
} from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import axios from "axios";
import { Parser } from "json2csv";
import DataGridMixin from "@/mixins/DataGridMixin";
import moment from "moment";

export default {
  components: {
    DxDataGrid,
    DxPaging,
    DxFilterRow,
    DxPager,
    DxExport,
    DxGrouping,
    DxGroupPanel,
    DxLoadPanel,
  },
  mixins: [DataGridMixin],
  props: {
    // eingebettet in die Seite Exporte: ohne eigene Leiste, Filterzeile, Pager, Ladeanzeige und Fehlerzeile
    embedded: { type: Boolean, default: false },
    // Berichtsmonat 'YYYY-MM' der Seite Exporte
    reportMonth: { type: String, default: null },
  },
  computed: {
    monthFormatted() {
      return moment(this.month).format("MM.YYYY");
    },
    dataUrl() {
      let toMonth = Number(this.month.substring(5, 7));
      let toYear = Number(this.month.substring(0, 4));
      let fromMonth = ((((toMonth - 2) % 12) + 12) % 12) + 1;
      let fromYear = fromMonth == 12 ? toYear - 1 : toYear;
      let fromDate = `${fromYear}-${fromMonth.toString().padStart(2, "0")}-15`;
      let toDate = `${toYear}-${toMonth.toString().padStart(2, "0")}-14`;
      return `${this.$store.state.baseUrl}ticketsale?customer_id=${this.$store.state.customerId}&from_date=${fromDate}&to_date=${toDate}&option=rboExport`;
    },
    columns() {
      return [
        { dataField: "id" },
        { dataField: "LINIE" },
        { dataField: "FAHRT" },
        { dataField: "EINSTIEG" },
        { dataField: "AUSSTIEG" },
        { dataField: "EINSTIEG_NAME" },
        { dataField: "AUSSTIEG_NAME" },
        { dataField: "AUSSENSTELLE" },
        { dataField: "DATUM" },
        { dataField: "STUNDE" },
        { dataField: "MINUTE" },
        { dataField: "ABRECHMONAT" },
        { dataField: "ABRECHJAHR" },
        { dataField: "ANZAHLKARTEN" },
        {
          caption: "GATTUNG",
          allowFiltering: true,
          calculateCellValue: (value) => {
            // Skip conversion for customer_id = 11
            if (Number(this.$store.state.customerId) === 11) {
              return value.GATTUNG;
            }

            switch (value.GATTUNG) {
              case 1:
                return 1;
              case 2:
                return 2;
              case 3:
                return 131;
              case 4:
                return 132;
              case 5:
                return 5;
              case 6:
                return 20;
              case 7:
                return 8;
              case 8:
                return 10;
              case 9:
                return 7;
              case 10:
                return 9;
              case 13:
                return 230;
              case 16:
                return 36;
              case 17:
                return 28;
              case 18:
                return 29;
              case 19:
                return 138;
              case 20:
                return 35;
              case 29:
                return 163;
              case 30:
                return 237;
              case 61:
                return 13;
              default:
                return 0;
            }
          },
        },
        { dataField: "TARIFGEBIET" },
        { dataField: "BETRAG" },
        { dataField: "FAHRER" },
        { dataField: "VERKAUFSID" },
        { dataField: "STORNOBELEGNUMMER" },
      ];
    },
  },
  watch: {
    dataUrl: function () {
      this.refreshDataGrid();
      this.collapseAllClick();
    },
    reportMonth(value) {
      if (value && value !== this.month) {
        this.month = value;
      }
    },
  },
  data() {
    return {
      month: this.reportMonth || new Date().toISOString().substr(0, 7),
      menu: false,
      expanded: true,
      searchText: "",
      gridRefName: "rboExportDataGrid",
      datasource: new CustomStore({
        load: () => axios.get(this.dataUrl),
      }),

      summary: {
        totalItems: [
          {
            column: "Betrag",
            summaryType: "sum",
            valueFormat: "#######0.00 €",
            displayFormat: "Gesamtsumme: {0}",
          },
        ],
      },
    };
  },
  methods: {
    refreshDataGrid() {
      const grid = this.$refs[this.gridRefName];
      if (grid && grid.instance) {
        grid.instance.refresh();
      }
    },
    onSearchInput() {
      if (this.$refs[this.gridRefName] && this.$refs[this.gridRefName].instance) {
        this.$refs[this.gridRefName].instance.searchByText(this.searchText || '');
      }
    },
    collapseAllClick() {
      this.expanded = !this.expanded;
    },
    async exportToCsv() {
      let data = await this.$refs[this.gridRefName].instance
        .getController("export")
        ._getAllItems(null)
        .promise();
      data = data
        .filter((item) => item.rowType === "data")
        .map((item) => item.data)
        .map((item) => {
          item.BETRAG = item.BETRAG.replace(".", ",");
          item.VERKAUFSID = "\t" + item.VERKAUFSID;

          // Skip GATTUNG conversion for customer_id = 11
          if (Number(this.$store.state.customerId) !== 11) {
            item.GATTUNG = ((originalGattung) => {
              switch (originalGattung) {
                case 1:
                  return 1;
                case 2:
                  return 2;
                case 3:
                  return 131;
                case 4:
                  return 132;
                case 5:
                  return 5;
                case 6:
                  return 20;
                case 7:
                  return 8;
                case 8:
                  return 10;
                case 9:
                  return 7;
                case 10:
                  return 9;
                case 13:
                  return 230;
                case 16:
                  return 36;
                case 17:
                  return 28;
                case 18:
                  return 29;
                case 19:
                  return 138;
                case 20:
                  return 35;
                case 29:
                  return 163;
                case 30:
                  return 237;
                case 61:
                  return 13;
                default:
                  return 0;
              }
            })(item.GATTUNG);
          }

          return item;
        });
      const fields = [
        "id",
        "LINIE",
        "FAHRT",
        "EINSTIEG",
        "AUSSTIEG",
        "EINSTIEG_NAME",
        "AUSSTIEG_NAME",
        "AUSSENSTELLE",
        "DATUM",
        "STUNDE",
        "MINUTE",
        "ABRECHMONAT",
        "ABRECHJAHR",
        "ANZAHLKARTEN",
        "GATTUNG",
        "TARIFGEBIET",
        "BETRAG",
        "FAHRER",
        "VERKAUFSID",
        "STORNOBELEGNUMMER",
      ];
      const opts = { fields, delimiter: ";", quote: "" };
      const parser = new Parser(opts);
      const csv = parser.parse(data);
      this.download(`rboexport.csv`, csv);
    },
    download(filename, text) {
      var element = document.createElement("a");
      element.setAttribute(
        "href",
        "data:text/plain;charset=utf-8," + encodeURIComponent(text)
      );
      element.setAttribute("download", filename);
      element.style.display = "none";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    },
    onToolbarPreparing() {
      // Buttons are now in the filter row, no toolbar buttons needed
    },
  },
};
</script>

<style scoped>
/* eingebettet (Exporte): Flex-Spalte mit voller Höhe, das Grid füllt sie */
.export-embedded {
  display: flex;
  flex-direction: column;
  height: 100%;
  text-align: left;
}
</style>
